import React, { AriaRole } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconAccessibilityOutline } from '@saucelabs/ds-icons';
import { Tooltip } from '@saucelabs/ds-tooltip';

import { TOOLBAR_TRACKING_NAMES, trackEvent } from '../../../tracking';
import * as globalActions from '../../../actions';
import * as globalSelectors from '../../../selectors';
import selectors from '../selectors';
import GenericButton from './GenericButton';
import Styles from './ButtonAccessibilityAudio.styles';

const AccessibilityAudioButton: React.FC<{ role?: AriaRole }> = ({ role }) => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const {
    deviceToolsDisabled,
    deviceType,
    testType,
  } = useSelector(selectors.toolbarButtonSelector);
  const {
    isAccessibilityAudioEnabled,
    isAndroidDevice,
  } = useSelector(globalSelectors.mobileSessionSelector);
  const axService = isAndroidDevice ? 'TalkBack' : 'VoiceOver';

  const toggleAccessibilityAudio = () => {
    void dispatch(globalActions.toggleAccessibilityAudio(axService));
    trackEvent(TOOLBAR_TRACKING_NAMES.ACCESSIBILITY_AUDIO, {
      deviceType,
      testType,
      value: !isAccessibilityAudioEnabled,
    });
  };

  const talkBackTooltip = (
    <div className="flex flex-col">
      <div className="self-center">
        Navigate with
      </div>
      <div className="flex items-end mt-1">
        <div className="flex flex-col mr-1">
          <div className={Styles['up-key-wrapper']}>
            <span className={Styles.key}>&#9206;</span>
          </div>
          <div>
            <span className={Styles.key}>&#9204;</span>
            <span className={Styles['down-key']}>&#9207;</span>
            <span className={Styles.key}>&#9205;</span>
          </div>
        </div>
        <div>
          <span className={Styles['enter-key']}>ENTER</span>
        </div>
      </div>
    </div>
  );

  const voiceOverTooltip = (
    <div className="flex flex-col">
      <div className="self-center">
        Navigate with
      </div>
      <div className="flex items-end mt-1">
        <div className="mr-1">
          <span className={Styles['left-key']}>&#9204;</span>
          <span className={Styles.key}>&#9205;</span>
        </div>
        <div>
          <span className={Styles['enter-key']}>ENTER</span>
        </div>
      </div>
    </div>
  );

  const accessibilityAudioOff = `Use ${axService} to read out the description of your app for Accessibility Testing.`;
  const accessibilityAudioOn = isAndroidDevice ? talkBackTooltip : voiceOverTooltip;

  return (
    <Tooltip
      placement="end"
      title={isAccessibilityAudioEnabled ? accessibilityAudioOn : accessibilityAudioOff}
    >
      <span>
        <GenericButton
          disabled={deviceToolsDisabled}
          icon={IconAccessibilityOutline}
          label={axService}
          onPress={toggleAccessibilityAudio}
          role={role}
          status={isAccessibilityAudioEnabled}
        />
      </span>
    </Tooltip>
  );
};

AccessibilityAudioButton.displayName = 'AccessibilityAudioButton';

export default AccessibilityAudioButton;
